import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Download, ScrollText, Search, Loader2, BookOpen, MessageCircleQuestion, X, Sparkles, Hash, Gavel, Copy, Check } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { log } from "../lib/log";

export default function Laws() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const isAr = lang === "ar";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const kind = searchParams.get("kind") || "";
  const [items, setItems] = useState([]);
  const [topQs, setTopQs] = useState([]);
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("title"); // "title" | "content" | "ai"
  const [contentHits, setContentHits] = useState([]);
  const [aiHits, setAiHits] = useState([]);
  const [articleHits, setArticleHits] = useState([]);
  const [aiAvailable, setAiAvailable] = useState(true);
  const [aiCategory, setAiCategory] = useState("");
  const [searching, setSearching] = useState(false);
  const [yearFilter, setYearFilter] = useState("");

  useEffect(() => {
    api.get("/laws").then((r) => setItems(r.data)).catch(() => {});
    api.get("/consultant/top-questions", { params: { limit: 8 } })
      .then((r) => setTopQs(r.data || [])).catch(() => {});
  }, []);

  const t = useMemo(() => ({
    eyebrow: isAr ? "المكتبة القانونية" : "Legal Library",
    title: isAr
      ? "المكتبة القانونية الكويتية، في متناولك."
      : "The Kuwaiti Legal Library, at your fingertips.",
    lead: isAr
      ? "مجموعة منتقاة من النصوص التشريعية المعمول بها في دولة الكويت، نوفّرها لعملائنا والمهتمين بالشأن القانوني للمراجعة المباشرة."
      : "A curated collection of Kuwaiti legislative texts, made available to our clients and the legal community for direct reference.",
    searchTitle: isAr ? "ابحث في عناوين القوانين..." : "Search by title…",
    searchContent: isAr ? "ابحث داخل نصوص القوانين..." : "Search inside the law texts…",
    searchAi: isAr
      ? "صف مشكلتك بكلماتك… مثال: «فُصلت من العمل بدون إنذار»"
      : "Describe your issue… e.g., 'I was dismissed without notice'",
    empty: isAr ? "لم تُرفع أي قوانين بعد." : "No laws have been published yet.",
    download: isAr ? "تحميل PDF فاخر" : "Branded PDF",
    read: isAr ? "اقرأ النص" : "Read text",
    open: isAr ? "افتح القانون" : "Open law",
    chars: (n) => {
      const v = Number(n) || 0;
      return isAr ? `${v.toLocaleString("ar")} حرف` : `${v.toLocaleString()} chars`;
    },
    modeTitle: isAr ? "في العنوان" : "In title",
    modeContent: isAr ? "داخل النص" : "In text",
    modeAi: isAr ? "بحث ذكي" : "AI search",
    aiBadge: isAr ? "AI" : "AI",
    aiHint: isAr
      ? "البحث الذكي يفهم معنى سؤالك ويعرض المواد القانونية ذات الصلة، حتى لو لم تُذكر الكلمات نفسها."
      : "AI search understands the meaning of your question and surfaces the most relevant legal articles, even if you don't use the exact terms.",
    aiTypeMore: isAr ? "اكتب 3 أحرف على الأقل لبدء البحث الذكي." : "Type at least 3 characters to start the AI search.",
    noAiHits: isAr
      ? "لم نجد مواد قريبة من سؤالك. جرّب صياغة أخرى أو كلمات أعمّ."
      : "No close matches. Try rephrasing with broader terms.",
    aiUnavailable: isAr
      ? "البحث الذكي غير متاح حالياً. الرجاء استخدام البحث في العنوان أو داخل النص."
      : "AI search is temporarily unavailable. Please use title or in-text search.",
    matchedArticle: isAr ? "أُطلب من:" : "Matched in:",
    article: isAr ? "مادة" : "Article",
    relevance: isAr ? "صلة" : "relevance",
    noContentHits: isAr ? "لا توجد نتائج تطابق بحثك داخل النصوص." : "No matches inside the law texts.",
    typeMore: isAr ? "اكتب حرفين على الأقل للبحث داخل النصوص." : "Type at least 2 characters to search inside texts.",
    snippetHeader: isAr ? "مقتطف:" : "Snippet:",
    matches: isAr ? "تطابقات:" : "matches:",
    popularEyebrow: isAr ? "قوانين بارزة" : "Featured",
    popularTitle: isAr ? "نصوص يطّلع عليها كثيراً" : "Frequently consulted",
    popularLead: isAr ? "اختيارات من المكتبة القانونية يطّلع عليها كثير من الزوار." : "Highlights from the legal library.",
    faqEyebrow: isAr ? "أسئلة متكررة" : "Frequently asked",
    faqTitle: isAr ? "ما يسأله الناس سامي." : "What people ask Sami.",
    faqLead: isAr
      ? "أكثر الأسئلة تكراراً على المستشار الذكي «سامي». اضغط أي سؤال لتجربته."
      : "The most-repeated questions sent to Sami AI. Tap any to try it.",
    askedTimes: (n) => isAr ? `طُرح ${n} مرة` : `Asked ${n}×`,
    askSami: isAr ? "اسأل سامي" : "Ask Sami",
    colName: isAr ? "اسم القانون" : "Law name",
    colYear: isAr ? "سنة النشر" : "Year",
    colActions: isAr ? "" : "",
    tabAll: isAr ? "الكل" : "All",
    tabLaws: isAr ? "القوانين" : "Laws",
    tabRegs: isAr ? "اللوائح والقرارات" : "Regulations",
  }), [isAr]);

  const runContentSearch = useCallback(async (text) => {
    const q = (text || "").trim();
    if (q.length < 2) {
      setContentHits([]);
      return;
    }
    setSearching(true);
    try {
      const r = await api.get(`/library/search`, { params: { q, limit: 30 } });
      const lawsOnly = (r.data?.hits || []).filter((h) => h.kind === "law");
      setContentHits(lawsOnly);
    } catch (e) {
      // Search is non-critical — empty results on failure is fine.
      log.debug("laws search failed", e?.message);
      setContentHits([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const runAiSearch = useCallback(async (text, cat) => {
    const q = (text || "").trim();
    if (q.length < 3) {
      setAiHits([]);
      setAnswer("");
      return;
    }
    setSearching(true);
    setAnswering(true);
    setAnswer("");
    try {
      const r = await api.get(`/library/consult`, {
        params: { q, limit: 8, category: (cat || "").trim() || undefined },
        timeout: 60000,
      });
      setAiHits(r.data?.hits || []);
      setAiAvailable(r.data?.available !== false);
      setAnswer(r.data?.answer || "");
    } catch (e) {
      log.debug("ai search failed", e?.message);
      setAiHits([]);
      setAiAvailable(false);
      setAnswer("");
    } finally {
      setSearching(false);
      setAnswering(false);
    }
  }, []);

  // Debounced content search
  useEffect(() => {
    if (mode !== "content") return;
    const handle = setTimeout(() => runContentSearch(query), 350);
    return () => clearTimeout(handle);
  }, [query, mode, runContentSearch]);

  // Article-level search (all modes): show the matching articles directly.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setArticleHits([]);
      return undefined;
    }
    let alive = true;
    const handle = setTimeout(() => {
      api
        .get("/library/article-search", { params: { q, limit: 30 } })
        .then((r) => { if (alive) setArticleHits(r.data?.hits || []); })
        .catch(() => { if (alive) setArticleHits([]); });
    }, 350);
    return () => { alive = false; clearTimeout(handle); };
  }, [query]);

  // Debounced AI semantic search (longer debounce — heavier endpoint)
  useEffect(() => {
    if (mode !== "ai") return;
    const handle = setTimeout(() => runAiSearch(query, aiCategory), 650);
    return () => clearTimeout(handle);
  }, [query, mode, aiCategory, runAiSearch]);

  const [categoryFilter, setCategoryFilter] = useState("");
  const [docFilter, setDocFilter] = useState(() => {
    const ty = searchParams.get("type");
    return ty === "law" || ty === "regulation" ? ty : "all";
  }); // all | law | regulation
  const [answer, setAnswer] = useState("");
  const [answering, setAnswering] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  // Keep the type/category filters in sync with the URL so selecting an item
  // from the header mega-menu (which only changes the query string) actually
  // re-filters — not just on first mount.
  useEffect(() => {
    const ty = searchParams.get("type");
    setDocFilter(ty === "law" || ty === "regulation" ? ty : "all");
    setCategoryFilter(searchParams.get("category") || "");
  }, [searchParams]);

  const copyArticle = useCallback((key, text) => {
    const done = () => {
      setCopiedId(key);
      toast.success(isAr ? "تم نسخ نص المادة" : "Article text copied");
      window.setTimeout(() => setCopiedId((c) => (c === key ? null : c)), 1600);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text || "").then(done).catch(done);
    } else {
      done();
    }
  }, [isAr]);

  // Classify an item as a regulation (لائحة / قرار) vs a law (قانون).
  const isRegulation = useCallback((l) => {
    if (l.doc_type) return l.doc_type === "regulation";
    if (l.document_kind === "regulation") return true;
    return /^\s*(اللائحة|لائحة|اللائحه|لائحه|القرار|قرار)/.test((l.title || "").trim());
  }, []);

  // Deep-link a search hit straight to its matched article inside the law.
  const hitTo = (h) =>
    h && h.article_number > 0 ? `/laws/${h.id}#article-${h.article_number}` : `/laws/${h.id}`;

  // Highlight law/article references inside the consultant's answer text.
  const highlightRefs = (txt) => {
    const parts = String(txt || "").split(
      /(الماد[ةه]\s*\(?\s*[\d٠-٩]+\s*\)?|قانون\s+رقم\s*\(?\s*[\d٠-٩]+\s*\)?\s*لسنة\s*[\d٠-٩]+)/g
    );
    return parts.map((p, i) =>
      /^(الماد|قانون\s+رقم)/.test(p) ? (
        <strong key={i} style={{ color: "var(--gold-deep)" }}>{p}</strong>
      ) : (
        <span key={i}>{p}</span>
      )
    );
  };

  const availableYears = useMemo(() => {
    const ys = new Set();
    items.forEach((l) => {
      const y = l.law_year || l.year;
      if (y && /^\d{4}$/.test(String(y))) ys.add(String(y));
    });
    return Array.from(ys).sort((a, b) => Number(b) - Number(a));
  }, [items]);

  const availableCategories = useMemo(() => {
    const cs = new Map();
    items.forEach((l) => {
      if (l.category) cs.set(l.category, (cs.get(l.category) || 0) + 1);
    });
    return Array.from(cs.entries()).sort((a, b) => b[1] - a[1]);
  }, [items]);

  const filteredByTitle = useMemo(() => {
    const q = query.trim().toLowerCase();
    const isConst = (l) => (l.category || "") === "دستوري";
    return items
      .filter((l) => {
        if (kind && (l.document_kind || "") !== kind) return false;
        // "القوانين" tab mirrors the mega-menu: excludes regulations AND the
        // constitution documents (those live under the الدستور category).
        if (docFilter === "law" && (isRegulation(l) || isConst(l))) return false;
        if (docFilter === "regulation" && !isRegulation(l)) return false;
        if (categoryFilter && (l.category || "") !== categoryFilter) return false;
        if (yearFilter && String(l.law_year || "") !== yearFilter) return false;
        if (!q) return true;
        return (l.title || "").toLowerCase().includes(q);
      })
      .sort((a, b) => {
        const ya = Number(a.law_year) || 0;
        const yb = Number(b.law_year) || 0;
        if (yb !== ya) return yb - ya;
        // Fallback: newer upload first
        return (b.uploaded_at || "").localeCompare(a.uploaded_at || "");
      });
  }, [items, kind, yearFilter, query, categoryFilter, docFilter, isRegulation]);

  const docCounts = useMemo(() => {
    let laws = 0, regs = 0;
    items.forEach((l) => {
      if (isRegulation(l)) regs++;
      else if ((l.category || "") === "دستوري") { /* constitution: not a "law" tab item */ }
      else laws++;
    });
    return { laws, regs, all: items.length };
  }, [items, isRegulation]);

  return (
    <div data-testid="laws-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-7">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-5 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
          <Link
            to="/judgments"
            data-testid="laws-to-principles"
            className="mt-5 inline-flex items-center gap-2 px-5 h-11 text-xs uppercase tracking-[0.18em] rounded-sm transition-colors"
            style={{ background: "var(--gold-deep)", color: "#fff" }}
          >
            <Gavel className="w-4 h-4" />
            {isAr ? "مبادئ محكمة التمييز" : "Court of Cassation Principles"}
          </Link>
        </div>
      </div>

      {/* TRENDING LAWS section removed (Feb 2026) — was based on
          visitor download counts which we no longer surface to the public. */}

      {/* TOP QUESTIONS (FAQ) — removed from the library per request; focus on the library itself */}
      {false && (
        <section data-testid="top-questions-section" className="mb-16">
          <div className="flex items-end justify-between mb-6 gap-4">
            <div>
              <p className="eyebrow inline-flex items-center gap-2">
                <MessageCircleQuestion
                  className="w-3.5 h-3.5"
                  style={{ color: "var(--gold-deep)" }}
                />
                {t.faqEyebrow}
              </p>
              <h2 className="font-display text-2xl lg:text-3xl mt-2 tracking-tight">
                {t.faqTitle}
              </h2>
            </div>
            <p className="hidden md:block text-sm text-slate-500 max-w-sm text-end">
              {t.faqLead}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {topQs.map((q, i) => (
              <Link
                key={i}
                to={`/consultant?q=${encodeURIComponent(q.question)}`}
                data-testid={`top-question-${i}`}
                className="group border border-slate-200 p-5 bg-white hover:border-slate-900 transition-colors flex items-start gap-4"
              >
                <span
                  className="shrink-0 w-8 h-8 grid place-items-center rounded-full border text-xs"
                  style={{ borderColor: "var(--gold-deep)", color: "var(--gold-deep)" }}
                >
                  <MessageCircleQuestion className="w-3.5 h-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p
                    dir="auto"
                    className="text-sm leading-relaxed line-clamp-2 text-slate-800 group-hover:text-slate-900"
                  >
                    {q.question}
                  </p>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-slate-500 flex items-center justify-end gap-2">
                    <span
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ color: "var(--gold-deep)" }}
                    >
                      {t.askSami} →
                    </span>
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Active KIND filter chip */}
      {kind && (
        <div className="mb-5 flex items-center gap-2">
          <span className="text-xs uppercase tracking-[0.2em] text-slate-500">
            {isAr ? "تصفية:" : "Filter:"}
          </span>
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("kind");
              setSearchParams(next, { replace: true });
            }}
            data-testid="clear-kind-filter"
            className="inline-flex items-center gap-1.5 px-3 h-8 text-sm rounded-sm"
            style={{ background: "var(--beige)", color: "var(--gold-deep)" }}
          >
            {kind}
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Search controls */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 max-w-3xl">
        <div className="relative flex-1">
          {mode === "ai" ? (
            <Sparkles
              className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: "var(--gold-deep)" }}
            />
          ) : (
            <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          )}
          <Input
            dir="auto"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              mode === "ai"
                ? t.searchAi
                : mode === "content"
                ? t.searchContent
                : t.searchTitle
            }
            data-testid="laws-search"
            className={`rounded-sm h-11 ps-9 ${
              mode === "ai" ? "border-[var(--gold-deep)]/40 focus-visible:ring-[var(--gold-deep)]/30" : ""
            }`}
          />
          {searching ? (
            <Loader2
              className="w-4 h-4 absolute end-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin"
              data-testid="laws-search-loader"
            />
          ) : null}
        </div>
        <div
          role="tablist"
          className="inline-flex border border-slate-200 bg-white rounded-sm overflow-hidden self-start"
        >
          <button
            type="button"
            data-testid="laws-search-mode-title"
            onClick={() => setMode("title")}
            className={`px-4 h-11 text-xs uppercase tracking-[0.18em] transition-colors ${
              mode === "title" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t.modeTitle}
          </button>
          <button
            type="button"
            data-testid="laws-search-mode-content"
            onClick={() => setMode("content")}
            className={`px-4 h-11 text-xs uppercase tracking-[0.18em] border-s border-slate-200 transition-colors ${
              mode === "content" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t.modeContent}
          </button>
          <button
            type="button"
            data-testid="laws-search-mode-ai"
            onClick={() => {
              if (!user) {
                navigate(`/login?next=${encodeURIComponent("/laws")}`);
                return;
              }
              setMode("ai");
            }}
            className={`px-4 h-11 text-xs uppercase tracking-[0.18em] border-s border-slate-200 transition-colors inline-flex items-center gap-1.5 ${
              mode === "ai"
                ? "text-white"
                : "text-slate-600 hover:bg-slate-50"
            }`}
            style={mode === "ai" ? { background: "var(--gold-deep)" } : {}}
          >
            <Sparkles className="w-3 h-3" />
            {t.modeAi}
          </button>
        </div>
      </div>

      {mode === "ai" && (
        <div className="mb-8 max-w-3xl" data-testid="laws-ai-hint">
          <div
            className="border-s-2 ps-4 text-sm leading-relaxed text-slate-600"
            style={{ borderColor: "var(--gold-deep)" }}
          >
            <span className="font-display text-slate-900">
              {isAr ? "البحث الذكي بالـ AI" : "AI semantic search"}
            </span>
            <span className="mx-2 text-slate-300">·</span>
            {t.aiHint}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2 ps-4">
            <span className="text-xs uppercase tracking-[0.18em] text-slate-500">
              {isAr ? "حصر الإجابة ضمن تصنيف:" : "Limit answer to category:"}
            </span>
            <select
              value={aiCategory}
              onChange={(e) => setAiCategory(e.target.value)}
              data-testid="laws-ai-category"
              className="h-9 rounded-sm border border-slate-300 bg-white px-3 text-sm"
            >
              <option value="">{isAr ? "كل التصنيفات" : "All categories"}</option>
              {availableCategories.map(([c, n]) => (
                <option key={c} value={c}>
                  {c} ({n})
                </option>
              ))}
            </select>
            {aiCategory && (
              <button
                type="button"
                onClick={() => setAiCategory("")}
                data-testid="laws-ai-category-clear"
                className="text-xs text-slate-400 hover:text-slate-700 underline"
              >
                {isAr ? "مسح" : "clear"}
              </button>
            )}
          </div>
        </div>
      )}

      {query.trim().length >= 2 && articleHits.length > 0 && (
        <section data-testid="laws-article-hits" className="mb-8 border border-slate-200 bg-white" dir="rtl">
          <h2 className="px-5 py-3 text-sm font-semibold border-b border-slate-200" style={{ color: "var(--gold-deep)" }}>
            {isAr ? `المواد المطابقة (${articleHits.length})` : `Matching articles (${articleHits.length})`}
          </h2>
          <ul className="divide-y divide-slate-200">
            {articleHits.map((h) => (
              <li key={`${h.id}-${h.seq}`} data-testid={`article-hit-${h.id}-${h.seq}`} className="p-5 hover:bg-slate-50 transition-colors">
                <Link to={hitTo(h)} className="font-display text-base leading-snug hover:text-[var(--gold-deep)] transition-colors">
                  {isAr ? "المادة" : "Art."} {h.article_number}{h.flag ? " ؟" : ""} — {h.title}
                </Link>
                <p className="mt-2 text-sm text-slate-700 leading-[1.9] line-clamp-3">{h.snippet}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* AI-MODE results */}
      {mode === "ai" ? (
        <>
          {!aiAvailable ? (
            <div
              className="border border-dashed border-slate-300 p-16 text-center text-slate-500"
              data-testid="laws-ai-unavailable"
            >
              {t.aiUnavailable}
            </div>
          ) : query.trim().length < 3 ? (
            <div
              className="border border-dashed border-slate-300 p-16 text-center text-slate-500"
              data-testid="laws-ai-prompt"
            >
              {t.aiTypeMore}
            </div>
          ) : aiHits.length === 0 && !searching ? (
            <div
              className="border border-dashed border-slate-300 p-16 text-center text-slate-500"
              data-testid="laws-ai-empty"
            >
              {t.noAiHits}
            </div>
          ) : (
            <>
              {answer && (
                <div
                  data-testid="laws-ai-answer"
                  className="mb-6 p-6 border-s-4 rounded-sm"
                  style={{ borderColor: "var(--gold-deep)", background: "rgba(201,169,97,0.06)" }}
                >
                  <div className="flex items-center gap-2 mb-2" style={{ color: "var(--gold-deep)" }}>
                    <Sparkles className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-[0.18em] font-medium">
                      {isAr ? "رأي المستشار القانوني" : "Legal consultant view"}
                    </span>
                  </div>
                  <p dir="rtl" className="text-[15px] leading-[2] text-slate-800 whitespace-pre-line">
                    {highlightRefs(answer)}
                  </p>
                  <p className="mt-3 text-xs text-slate-500" dir="rtl">
                    {isAr
                      ? "المصادر أدناه — اضغط على اسم القانون أو المادة للانتقال إلى نصها."
                      : "Sources below — tap a law or article to open its text."}
                  </p>
                </div>
              )}
              <ul
                className="border border-slate-200 divide-y divide-slate-200 bg-white"
                data-testid="laws-ai-results"
              >
              {aiHits.map((h) => (
                <li
                  key={`${h.id}-${h.article_number}`}
                  data-testid={`law-ai-hit-${h.id}`}
                  className="p-6 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="shrink-0 w-10 h-10 grid place-items-center"
                      style={{
                        background: "var(--beige)",
                        color: "var(--gold-deep)",
                      }}
                    >
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span
                          className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm"
                          style={{
                            background: "var(--gold-deep)",
                            color: "#fff",
                          }}
                        >
                          {t.aiBadge}
                        </span>
                        {h.article_number > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                            <Hash className="w-3 h-3" />
                            {t.article} {h.article_number}
                          </span>
                        )}
                        <span className="text-[11px] text-slate-400">
                          · {t.relevance} {(h.score * 100).toFixed(0)}%
                        </span>
                      </div>
                      <Link
                        to={hitTo(h)}
                        className="font-display text-lg leading-tight block hover:text-[var(--gold-deep)] transition-colors"
                        dir="rtl"
                      >
                        {h.title}
                      </Link>
                      <div
                        data-testid={`law-ai-fulltext-${h.id}`}
                        className="mt-3 text-[15px] text-slate-800 leading-[2] whitespace-pre-wrap rounded-sm border border-slate-200 bg-slate-50/60 p-4 max-h-80 overflow-y-auto"
                        dir="rtl"
                      >
                        {h.text || h.snippet}
                      </div>
                      <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <Link
                          to={hitTo(h)}
                          data-testid={`law-ai-open-${h.id}`}
                          className="inline-flex items-center gap-2 px-4 h-10 text-xs uppercase tracking-[0.18em] rounded-sm transition-colors"
                          style={{ background: "var(--navy)", color: "#fff" }}
                        >
                          <BookOpen className="w-3.5 h-3.5" /> {h.article_number > 0 ? (isAr ? `اذهب للمادة ${h.article_number} داخل القانون` : `Go to Art. ${h.article_number}`) : t.read}
                        </Link>
                        <button
                          type="button"
                          onClick={() => copyArticle(`${h.id}-${h.article_number}`, h.text || h.snippet)}
                          data-testid={`law-ai-copy-${h.id}`}
                          className="inline-flex items-center gap-2 px-4 h-10 text-xs uppercase tracking-[0.18em] border transition-colors hover:bg-[var(--beige)]"
                          style={{ borderColor: "var(--gold-deep)", color: copiedId === `${h.id}-${h.article_number}` ? "#059669" : "var(--gold-deep)" }}
                        >
                          {copiedId === `${h.id}-${h.article_number}` ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {copiedId === `${h.id}-${h.article_number}` ? (isAr ? "تم النسخ" : "Copied") : (isAr ? "نسخ المادة" : "Copy article")}
                        </button>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/laws/${h.id}/branded-pdf`}
                          className="inline-flex items-center gap-2 px-4 h-10 text-xs uppercase tracking-[0.18em] border border-slate-200 hover:border-slate-900 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" /> {t.download}
                        </a>
                        <button
                          type="button"
                          data-testid={`law-ai-asksami-${h.id}`}
                          onClick={() => {
                            const art = h.article_number > 0 ? (isAr ? ` المادة ${h.article_number}.` : ` Art. ${h.article_number}.`) : ".";
                            const q = (isAr ? `بخصوص «${h.title}»` : `Regarding "${h.title}"`) + art + (isAr ? " اشرح لي الحكم وما ينطبق منه مع ذكر المواد ذات الصلة." : " Explain it.");
                            navigate(`/consultant?q=${encodeURIComponent(q)}&ask=1`);
                          }}
                          className="inline-flex items-center gap-2 px-4 h-10 text-xs uppercase tracking-[0.18em] rounded-sm transition-colors"
                          style={{ background: "var(--gold-deep)", color: "#fff" }}
                        >
                          <Sparkles className="w-3.5 h-3.5" /> {t.askSami}
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            </>
          )}
        </>
      ) : mode === "content" ? (
        <>
          {query.trim().length < 2 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500" data-testid="laws-content-prompt">
              {t.typeMore}
            </div>
          ) : contentHits.length === 0 && !searching ? (
            <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500" data-testid="laws-content-empty">
              {t.noContentHits}
            </div>
          ) : (
            <ul className="border border-slate-200 divide-y divide-slate-200 bg-white" data-testid="laws-content-results">
              {contentHits.map((h) => (
                <li
                  key={h.id}
                  data-testid={`law-hit-${h.id}`}
                  className="p-6 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-10 h-10 grid place-items-center border border-slate-200">
                      <ScrollText className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-lg leading-tight">{h.title}</p>
                      <p className="text-xs text-slate-500 mt-1.5">
                        {h.language === "ar" ? "AR" : "EN"} · {t.matches} {h.score}
                      </p>
                      <p className="mt-3 text-sm text-slate-700 leading-relaxed line-clamp-3" dir="auto">
                        <span className="text-slate-400 me-2">{t.snippetHeader}</span>
                        {h.snippet}
                      </p>
                      <Link
                        to={hitTo(h)}
                        data-testid={`law-hit-open-${h.id}`}
                        className="mt-4 inline-flex items-center gap-2 px-4 h-10 text-xs uppercase tracking-[0.18em] rounded-sm transition-colors"
                        style={{ background: "var(--navy)", color: "#fff" }}
                      >
                        <BookOpen className="w-3.5 h-3.5" /> {t.read}
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        // TITLE-MODE results
        <>
          <div
            className="flex flex-wrap items-center justify-between gap-4 mb-6"
            data-testid="laws-toolbar"
          >
            <p className="text-sm text-slate-600" data-testid="laws-count">
              {isAr
                ? `${filteredByTitle.length.toLocaleString("ar")} من أصل ${items.length.toLocaleString("ar")} قانون`
                : `${filteredByTitle.length.toLocaleString()} of ${items.length.toLocaleString()} laws`}
            </p>
            {availableCategories.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap w-full mb-1" data-testid="laws-category-filter">
                <span className="text-xs uppercase tracking-[0.18em] text-slate-500">
                  {isAr ? "التصنيف" : "Category"}
                </span>
                <button
                  type="button"
                  onClick={() => setCategoryFilter("")}
                  data-testid="laws-category-all"
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${categoryFilter === "" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"}`}
                >
                  {isAr ? "الكل" : "All"}
                </button>
                {availableCategories.map(([cat, count]) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategoryFilter(cat === categoryFilter ? "" : cat)}
                    data-testid={`laws-category-${cat}`}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${categoryFilter === cat ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"}`}
                  >
                    {cat} <span className="opacity-60">({count.toLocaleString(isAr ? "ar" : "en")})</span>
                  </button>
                ))}
              </div>
            )}
            {availableYears.length > 0 && (
              <div className="flex items-center gap-2">
                <label
                  htmlFor="laws-year-filter"
                  className="text-xs uppercase tracking-[0.18em] text-slate-500"
                >
                  {isAr ? "السنة" : "Year"}
                </label>
                <select
                  id="laws-year-filter"
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  data-testid="laws-year-filter"
                  className="h-10 px-3 bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-slate-900"
                >
                  <option value="">{isAr ? "الكل" : "All"}</option>
                  {availableYears.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                {yearFilter && (
                  <button
                    type="button"
                    onClick={() => setYearFilter("")}
                    data-testid="laws-year-clear"
                    className="text-xs text-slate-500 hover:text-slate-900 underline"
                  >
                    {isAr ? "مسح" : "clear"}
                  </button>
                )}
              </div>
            )}
          </div>
          {/* Law / Regulation tabs */}
          <div className="mb-5 inline-flex border border-slate-200 bg-white rounded-sm overflow-hidden" data-testid="laws-doctype-tabs">
            {[
              ["all", t.tabAll, docCounts.all],
              ["law", t.tabLaws, docCounts.laws],
              ["regulation", t.tabRegs, docCounts.regs],
            ].map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                data-testid={`laws-doctype-${key}`}
                onClick={() => setDocFilter(key)}
                className={`px-4 h-10 text-xs uppercase tracking-[0.16em] transition-colors border-s border-slate-200 first:border-s-0 ${
                  docFilter === key ? "text-white" : "text-slate-600 hover:bg-slate-50"
                }`}
                style={docFilter === key ? { background: "var(--navy)" } : {}}
              >
                {label} <span className="opacity-60">({(count || 0).toLocaleString(isAr ? "ar" : "en")})</span>
              </button>
            ))}
          </div>

          {filteredByTitle.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500">
              {t.empty}
            </div>
          ) : (
            <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="laws-table-wrap">
              <table className="w-full border-collapse" data-testid="laws-table">
                <thead>
                  <tr className="text-start border-b border-slate-200 bg-slate-50/80">
                    <th className="text-start font-sans text-[11px] uppercase tracking-[0.16em] text-slate-500 px-6 py-3">
                      {t.colName}
                    </th>
                    <th className="text-start font-sans text-[11px] uppercase tracking-[0.16em] text-slate-500 px-4 py-3 whitespace-nowrap">
                      {t.colYear}
                    </th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredByTitle.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`law-item-${l.id}`}
                      className="hover:bg-slate-50 transition-colors align-top"
                    >
                      <td className="px-6 py-4">
                        <Link
                          to={`/laws/${l.id}`}
                          data-testid={`law-title-link-${l.id}`}
                          className="font-display text-base lg:text-lg leading-snug hover:text-[var(--gold-deep)] transition-colors inline-flex items-start gap-2"
                          dir="rtl"
                        >
                          <ScrollText className="w-4 h-4 mt-1 shrink-0 text-slate-400" />
                          <span>
                            {l.title}
                            {l.text_quality === "needs_review" && (
                              <span
                                data-testid={`law-needs-review-${l.id}`}
                                title={isAr ? "قد يحتوي على أخطاء OCR" : "May contain OCR typos"}
                                className="inline-block ms-2 align-middle text-[10px] font-sans uppercase tracking-[0.15em] px-2 py-0.5 border border-amber-300 text-amber-800 bg-amber-50 rounded-sm"
                              >
                                OCR
                              </span>
                            )}
                          </span>
                        </Link>
                        <div className="mt-1.5 flex items-center gap-2 flex-wrap ps-6" dir="rtl">
                          {l.status === "repealed" && (
                            <span
                              data-testid={`law-repealed-badge-${l.id}`}
                              title={l.status_note || ""}
                              className="inline-block text-[10px] font-sans px-2 py-0.5 rounded-full border border-red-300 text-red-800 bg-red-50"
                            >
                              {isAr ? "ملغى" : "Repealed"}
                            </span>
                          )}
                          {isRegulation(l) && (
                            <span
                              data-testid={`law-reg-badge-${l.id}`}
                              className="inline-block text-[10px] font-sans px-2 py-0.5 rounded-full text-white"
                              style={{ background: "var(--navy)" }}
                            >
                              {isAr ? "لائحة/قرار" : "Regulation"}
                            </span>
                          )}
                          {l.category && (
                            <span
                              data-testid={`law-category-badge-${l.id}`}
                              className="inline-block text-[10px] font-sans px-2 py-0.5 rounded-full border"
                              style={{ borderColor: "var(--gold-deep)", color: "var(--gold-deep)", background: "rgba(201,169,97,0.08)" }}
                            >
                              {l.category}
                            </span>
                          )}
                          <span className="text-[11px] text-slate-400">
                            {l.language === "ar" ? "AR" : "EN"} · {t.chars(l.char_count)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap font-mono text-sm text-slate-700" data-testid={`law-year-${l.id}`}>
                        {l.law_year ? String(l.law_year) : "—"}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 justify-end">
                          <Link
                            to={`/laws/${l.id}`}
                            data-testid={`law-read-${l.id}`}
                            className="inline-flex items-center gap-2 px-3 h-9 text-[11px] uppercase tracking-[0.16em] rounded-sm transition-colors"
                            style={{ background: "var(--navy)", color: "#fff" }}
                          >
                            <BookOpen className="w-3.5 h-3.5" /> {t.read}
                          </Link>
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/laws/${l.id}/branded-pdf`}
                            data-testid={`law-download-${l.id}`}
                            className="inline-flex items-center gap-2 px-3 h-9 text-[11px] uppercase tracking-[0.16em] border border-slate-200 hover:border-slate-900 transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" /> PDF
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
